import { AsyncPipe, NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { RouterLink, RouterLinkActive } from '@angular/router'
import { Store } from '@ngrx/store'
import type { Observable } from 'rxjs'
import { of } from 'rxjs'

import { insertIf } from '@app/core/insert-if'
import { truthy } from '@app/core/types/truthy'
import * as environment from '@app/environment'
import { MemberService } from '@app/services/member.service'
import { SiteNavService } from '@app/services/site-nav.service'
import { SiteStore } from '@app/store/site.store'
import * as fromUnreadInteractions from '@store/unread-interactions'

import { AssetUrlPipe } from '../../../modules/assets/pipes/asset-url.pipe'
import { BreakpointAbovePipe } from '../../../modules/ui/pipes/breakpoint-above.pipe'
import { BreakpointBelowPipe } from '../../../modules/ui/pipes/breakpoint-below.pipe'
import type * as fromRoot from '../../../store/ngrx/root'
import { MemberHeaderAccountComponent } from '../header-account/header-account.component'

@Component({
  selector: 'sb-member-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    RouterLinkActive,
    NgClass,
    MemberHeaderAccountComponent,
    AsyncPipe,
    BreakpointAbovePipe,
    BreakpointBelowPipe,
    AssetUrlPipe,
  ],
})
export class MemberHeaderComponent {
  readonly memberService = inject(MemberService)
  readonly siteNavService = inject(SiteNavService)
  private readonly store = inject<Store<fromRoot.State>>(Store)
  private readonly siteStore = inject(SiteStore)

  readonly matchesDot$ = this.store.select(fromUnreadInteractions.Selectors.hasAnyMatches)
  private readonly messagesDot$ = this.store.select(fromUnreadInteractions.Selectors.hasAnyMessages)
  private readonly accessDot$ = this.store.select(fromUnreadInteractions.Selectors.hasAnyAccess)

  links: {
    name: string
    routerLink: string[]
    iconName?: string
    showIndicator$?: Observable<boolean>
    optional?: boolean
  }[] = [
    {
      name: 'Search',
      routerLink: ['next/search'],
      iconName: 'nc-search',
      showIndicator$: of(false),
    },
    {
      name: 'Messages',
      routerLink: ['next/messages'],
      iconName: 'nc-chat-o',
      showIndicator$: this.messagesDot$,
    },
    {
      name: 'Matches',
      routerLink: ['next/matches'],
      iconName: 'nc-heart-o',
      showIndicator$: this.matchesDot$,
    },
    ...insertIf(truthy(this.siteStore.features().private_content), {
      name: 'Access',
      routerLink: ['next/access'] as string[],
      iconName: 'nc-key',
      showIndicator$: this.accessDot$,
    }),
    {
      name: 'Profile',
      routerLink: ['next/me/about'],
      optional: true,
    },
  ]

  readonly environment = environment
}
